import type { ComboBoxProps } from "@react-types/combobox";
import { type ReactNode, useRef } from "react";
import { useButton, useComboBox, useFilter } from "react-aria";
import { useComboBoxState } from "react-stately";

import { ChevronDownIcon } from "@moment/icons";

import { Input } from "../Input";
import { cn } from "../utils";
import { ListBox } from "./Listbox";
import { Popover } from "./Popover";

export function Combobox<T extends object>(
	props: ComboBoxProps<T> & {
		open?: boolean;
		className?: string;
		popoverClassName?: string;
		endIcon?: ReactNode;
	}
) {
	let { contains } = useFilter({ sensitivity: "base" });
	let state = useComboBoxState({ ...props, defaultFilter: contains });

	let buttonRef = useRef(null);
	let inputRef = useRef(null);
	let listBoxRef = useRef(null);
	let popoverRef = useRef(null);

	let {
		buttonProps: triggerProps,
		inputProps: { size, type, color, ...inputProps },
		listBoxProps,
		labelProps,
	} = useComboBox(
		{
			...props,
			inputRef,
			buttonRef,
			listBoxRef,
			popoverRef,
		},
		state
	);

	let { buttonProps } = useButton(triggerProps, buttonRef);
	const open = props.open !== undefined ? props.open : state.isOpen;

	return (
		<div className={cn("inline-flex flex-col relative", props.className)}>
			{props.label && (
				<label
					{...labelProps}
					className="block text-sm font-medium text-gray-700 text-left"
				>
					{props.label}
				</label>
			)}
			<Input
				{...inputProps}
				type="text"
				ref={inputRef}
				endAddon={
					props.endIcon ? (
						props.endIcon
					) : (
						<button
							{...buttonProps}
							ref={buttonRef}
							className={cn(
								"addon",
								"shrink-0",
								"select-none",
								"cursor-default",
								"p-1"
							)}
						>
							<ChevronDownIcon aria-hidden="true" />
						</button>
					)
				}
			/>
			{open && (
				<Popover
					popoverRef={popoverRef}
					triggerRef={inputRef}
					state={state}
					isNonModal
					placement="bottom start"
					className={cn("min-w-52", props.popoverClassName)}
				>
					<ListBox {...listBoxProps} listBoxRef={listBoxRef} state={state} />
				</Popover>
			)}
		</div>
	);
}
